// ─── Static Data ─────────────────────────────────────────────────────────────
import type { CatalogIcon } from './types';
import { asset } from '@/utils/basePath';
export type { OUNode } from './types';

// OU data is centralised in mockOUs — import and re-export so existing consumers
// that import from this file continue to work without changes.
export {
  OU_TREE as OU_LIST,
  OU_COLORS,
  OU_PALETTE,
  OU_CATALOG,
  ACTIVE_OUS,
  ALL_OUS,
  getOUColor,
  getOULabel,
  getOU,
  findNode,
} from '@/data/mockOUs';

export const FUNDAMENTALS = [
  { id: 'f1', name: 'Pasión por el Consumidor', short: 'Pasión', color: '#F08521' },
  { id: 'f2', name: 'Esencialismo', short: 'Esencialismo', color: '#342F82' },
  { id: 'f3', name: 'Talent Ownership', short: 'Talent', color: '#40B8E9' },
  { id: 'f4', name: 'Seguridad Psicológica', short: 'Seguridad', color: '#20B15A' },
  { id: 'f5', name: 'Orientación a Resultados', short: 'Resultados', color: '#8B5CF6' },
];

export const BEHAVIORS: Record<string, Array<{ id: string; name: string }>> = {
  f1: [
    { id: 'f1b1', name: 'Escucha activa al consumidor' },
    { id: 'f1b2', name: 'Empatía en el servicio' },
    { id: 'f1b3', name: 'Orientación al cliente' },
  ],
  f2: [
    { id: 'f2b1', name: 'Foco en lo esencial' },
    { id: 'f2b2', name: 'Eliminación de lo trivial' },
    { id: 'f2b3', name: 'Claridad de propósito' },
  ],
  f3: [
    { id: 'f3b1', name: 'Desarrollo de talento' },
    { id: 'f3b2', name: 'Mentoring activo' },
    { id: 'f3b3', name: 'Feedback continuo' },
  ],
  f4: [
    { id: 'f4b1', name: 'Apertura al diálogo' },
    { id: 'f4b2', name: 'Respeto en desacuerdo' },
    { id: 'f4b3', name: 'Confianza en el equipo' },
  ],
  f5: [
    { id: 'f5b1', name: 'Orientación a métricas' },
    { id: 'f5b2', name: 'Cumplimiento de objetivos' },
  ],
};

export const BUILT_IN_ICONS = [
  { value: asset('/assets/pasionporconsumidor.svg'), label: 'Pasión por Consumidor' },
  { value: asset('/assets/wsensialismo.svg'),        label: 'Esencialismo' },
  { value: asset('/assets/talentownership.svg'),     label: 'Talent Ownership' },
  { value: asset('/assets/logo-fundamentales.svg'),  label: 'Fundamentales' },
];

// Day name keys — resolved via i18n at render time
export const DAY_SHORT_KEYS = [
  'admin.dayMonShort', 'admin.dayTueShort', 'admin.dayWedShort',
  'admin.dayThuShort', 'admin.dayFriShort', 'admin.daySatShort', 'admin.daySunShort',
] as const;
export const DAY_FULL_KEYS = [
  'admin.dayMon', 'admin.dayTue', 'admin.dayWed',
  'admin.dayThu', 'admin.dayFri', 'admin.daySat', 'admin.daySun',
] as const;
export const DAY_LETTER_KEYS = [
  'admin.dayLetterMon', 'admin.dayLetterTue', 'admin.dayLetterWed',
  'admin.dayLetterThu', 'admin.dayLetterFri', 'admin.dayLetterSat', 'admin.dayLetterSun',
] as const;

// LANG_MAP kept for fallback only — UI uses getLangName(lang, t) for i18n
export const LANG_MAP: Record<string, string> = {
  en: 'Inglés', de: 'Alemán', pt: 'Portugués', nl: 'Holandés',
  fr: 'Francés', ro: 'Rumano',
};

export const LANG_COLORS: Record<string, string> = {
  en: '#2563EB', pt: '#16A34A', fr: '#7C3AED',
  de: '#DC2626', nl: '#EA580C', ro: '#0891B2',
};

export const BUILDER_STEPS = [
  { id: 'info',      tKey: 'admin.builderNavInfo',      icon: 'fas fa-info-circle' },
  { id: 'audiencia', tKey: 'admin.builderNavAudiencia', icon: 'fas fa-users' },
  { id: 'preguntas', tKey: 'admin.builderNavPreguntas', icon: 'fas fa-question-circle' },
  { id: 'config',    tKey: 'admin.builderNavConfig',    icon: 'fas fa-cog' },
  { id: 'publicar',  tKey: 'admin.builderNavPublicar',  icon: 'fas fa-rocket' },
];

export const PREVIEW_PARTICIPANTS = [
  { name: 'Juan García',    initials: 'JG' },
  { name: 'María López',    initials: 'ML' },
  { name: 'Carlos Mendoza', initials: 'CM' },
  { name: 'Ana Torres',     initials: 'AT' },
];

export const PREVIEW_EMOJI = [
  { color: '#EF4444', label: '1', path: 'M12 26c2-3 10-3 12 0', eyeY: 15 },
  { color: '#F59E0B', label: '2', path: 'M12 25c2-2 10-2 12 0', eyeY: 15 },
  { color: '#10B981', label: '3', path: 'M12 22c2 2 10 2 12 0', eyeY: 15 },
  { color: '#342F82', label: '4', path: 'M11 21c2 4 12 4 14 0', eyeY: 14 },
];

export const ALL_LANGS = ['es', 'en', 'pt', 'fr', 'de', 'nl', 'ro'] as const;
export const BASIC_LANGS = ['es', 'en'] as const;

export const ICP_CATALOG_KEY = 'sigma_icon_catalog';

// ─── Utility helpers ─────────────────────────────────────────────────────────

export function getLangName(lang: string, t: (key: string) => string): string {
  const key = `admin.lang${lang.charAt(0).toUpperCase()}${lang.slice(1)}`;
  const translated = t(key);
  return translated !== key ? translated : (LANG_MAP[lang] || lang.toUpperCase());
}

export function getWeightLabel(w: number, t: (key: string) => string): string {
  if (w <= 2) return t('admin.builderWeightVeryLow');
  if (w <= 4) return t('admin.builderWeightLow');
  if (w <= 6) return t('admin.builderWeightMedium');
  if (w <= 8) return t('admin.builderWeightHigh');
  return t('admin.builderWeightVeryHigh');
}

export function newQuestion(): import('./types').Question {
  return {
    id: `q_${Date.now()}_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`,
    enfoque: 'individual' as const,
    presentMode: 'matrix' as const,
    type: 'rating' as const,
    fundamental: '',
    coreBehavior: '',
    text: '',
    sourceLang: 'es',
    icon: '',
    options: [],
    includeNoObs: true,
  };
}

export interface LangRequirement {
  /** Idiomas obligatorios (sin 'es') según la audiencia. */
  mandatory: string[];
  /** Idiomas disponibles pero no obligatorios (sin 'es'), desactivados por defecto. */
  optional: string[];
}

const NON_ES_LANGS = ALL_LANGS.filter(lang => lang !== 'es');

/** Conjunto de OUs efectivamente targeteadas, según el tipo de encuesta:
 *  - General: usa el árbol multi-OU (`audienceOUs`).
 *  - Personalizada por OU: usa `destOU` — `audienceOUs` no aplica a este flujo y siempre
 *    queda vacío, así que NO se puede usar para decidir "¿es global?" aquí (era el bug).
 *  - Personalizada por reunión/personas: audiencia puntual y conocida, nunca "global". */
function getEffectiveOuIds(form: import('./types').SurveyForm): string[] | null {
  if (form.surveyType === 'personalizada') {
    if (form.destMode === 'ou') return form.destOU ? [form.destOU] : [];
    return null;
  }
  return form.audienceOUs;
}

/**
 * Reglas de idioma por audiencia (confirmadas 2026-07-25):
 * - Global (sin OUs seleccionadas u OU/reunión/personas sin OUs resueltas): todos los idiomas de
 *   la plataforma son obligatorios (ver `getEffectiveOuIds` para encuestas 'personalizada').
 * - Europa (OU con flag GDPR seleccionada): los 6 idiomas no-español son obligatorios (sin cambios
 *   respecto al comportamiento previo a esta regla).
 * - Cualquier otra OU específica: solo Inglés es obligatorio; el resto queda disponible como opcional.
 * - Personalizada por reunión/personas: audiencia puntual y conocida — ningún idioma obligatorio,
 *   todos quedan disponibles como opcionales.
 */
export function getLangRequirement(
  form: import('./types').SurveyForm,
  gdprOuKey: string | undefined,
): LangRequirement {
  const effectiveOuIds = getEffectiveOuIds(form);
  if (effectiveOuIds === null) {
    return { mandatory: [], optional: [...NON_ES_LANGS] };
  }

  const isGlobal = effectiveOuIds.length === 0;
  const isEuropa = !!gdprOuKey && effectiveOuIds.includes(gdprOuKey);

  if (isGlobal || isEuropa) {
    return { mandatory: [...NON_ES_LANGS], optional: [] };
  }
  return {
    mandatory: ['en'],
    optional: NON_ES_LANGS.filter(lang => lang !== 'en'),
  };
}

export function getCatalog(): CatalogIcon[] {
  try { return JSON.parse(localStorage.getItem(ICP_CATALOG_KEY) || '[]'); } catch { return []; }
}

export function saveCatalog(catalog: CatalogIcon[]): void {
  try {
    localStorage.setItem(ICP_CATALOG_KEY, JSON.stringify(catalog));
  } catch {
    /* QuotaExceededError or storage unavailable — fail silently */
  }
}
